import React from 'react';
import {
  Image,
  ImageSourcePropType,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from 'react-router-dom';

import { useConsumerStatusSummaryQuery } from 'apollo/queries/autogenerate/hooks';
import { HoukatsuStatus } from 'apollo/queries/autogenerate/schemas';
import LogoPlus from 'shared/assets/images/logo-plus.png';
import LogoNonPlus from 'shared/assets/images/logo.png';
import PlusBadge from 'shared/assets/images/plus-badge@3x.png';
import Button from 'shared/modules/common/components/atoms/Button';
import eventBus from 'shared/utils/event-bus';
import Mixpanel from 'shared/vendor/Mixpanel';
import AccountIconActive from 'web/assets/images/account-active.png';
import AccountIcon from 'web/assets/images/account.png';
import HelpIconActive from 'web/assets/images/help-active.png';
import HelpIcon from 'web/assets/images/help.png';
import HomeIconActive from 'web/assets/images/home-active.png';
import HomeIcon from 'web/assets/images/home.png';
import SignoutIconActive from 'web/assets/images/signout-active.png';
import SignoutIcon from 'web/assets/images/signout.png';
import UsageIconActive from 'web/assets/images/usage-active.png';
import UsageIcon from 'web/assets/images/usage.png';
import { ACTION, ITEM, SCREEN } from 'web/modules/common/constants/mixpanel';
import routes from 'web/navigation/routes';

const Navigation = () => {
  const isBigScreen = useMediaQuery({ minWidth: 768 });

  const { data: consumerStatusData } = useConsumerStatusSummaryQuery({
    fetchPolicy: 'cache-only',
  });

  const isPlus =
    consumerStatusData?.houkatsuStatus.status === HoukatsuStatus.APPROVED ||
    consumerStatusData?.houkatsuStatus.status ===
      HoukatsuStatus.APPROVED_FOR_NLP;

  return (
    <>
      <View style={[style.root, isBigScreen && styleBigScreen.root]}>
        {isBigScreen && (
          <>
            <Image
              source={{ uri: isPlus ? LogoPlus : LogoNonPlus }}
              style={isPlus ? style.logoPlus : style.logoNonPlus}
            />
            {isPlus && (
              <Image source={{ uri: PlusBadge }} style={style.plusBadge} />
            )}
          </>
        )}
        <View
          style={[
            style.navigationLinks,
            isBigScreen && styleBigScreen.navigationLinks,
          ]}
        >
          <NavigationLinkItem
            to={routes.authenticated.home.root}
            icon={HomeIcon}
            iconActive={HomeIconActive}
            text="ホーム"
            onPress={() =>
              Mixpanel.trackEvent(`${SCREEN.NAVIGATION} - ${ACTION.CLICK}`, {
                Item: ITEM.HOME,
              })
            }
            id="navigation-link-home"
          />
          <NavigationLinkItem
            to={routes.authenticated.usage.root}
            icon={UsageIcon}
            iconActive={UsageIconActive}
            text="ご利用明細"
            onPress={() =>
              Mixpanel.trackEvent(`${SCREEN.NAVIGATION} - ${ACTION.CLICK}`, {
                Item: ITEM.USAGE,
              })
            }
            id="navigation-link-usage"
          />
          <NavigationLinkItem
            to={routes.authenticated.account.root}
            icon={AccountIcon}
            iconActive={AccountIconActive}
            text="アカウント"
            onPress={() =>
              Mixpanel.trackEvent(`${SCREEN.NAVIGATION} - ${ACTION.CLICK}`, {
                Item: ITEM.ACCOUNT,
              })
            }
            id="navigation-link-account"
          />
          {isBigScreen && (
            <>
              <NavigationLinkItem
                to={process.env.FAQ_URL}
                icon={HelpIcon}
                iconActive={HelpIconActive}
                text="よくあるご質問"
                openInNewTab
                onPress={() =>
                  Mixpanel.trackEvent(
                    `${SCREEN.NAVIGATION} - ${ACTION.CLICK}`,
                    {
                      Item: ITEM.HELP,
                    }
                  )
                }
                id="navigation-link-help"
              />
              <NavigationLinkItem
                icon={SignoutIcon}
                iconActive={SignoutIconActive}
                text="ログアウト"
                onPress={() => {
                  Mixpanel.trackEvent(
                    `${SCREEN.NAVIGATION} - ${ACTION.CLICK}`,
                    {
                      Item: ITEM.SIGNOUT,
                    }
                  );

                  eventBus.emit('signout:open');
                }}
                id="navigation-link-signout"
              />
            </>
          )}
        </View>
        {isBigScreen && (
          <Button
            variant="dark"
            title="アプリを取得する"
            titleStyle={style.buttonTitle}
            style={style.downloadButton}
            onPress={() => eventBus.emit('appdl:open')}
            id="app-download-link-button"
          />
        )}
      </View>
    </>
  );
};

type NavigationLinkItemProps = {
  to?: string;
  icon: ImageSourcePropType;
  iconActive: ImageSourcePropType;
  text: string;
  openInNewTab?: boolean;
  onPress?: () => void;
  id?: string;
};

const NavigationLinkItem = (props: NavigationLinkItemProps) => {
  const isBigScreen = useMediaQuery({ minWidth: 768 });
  const location = useLocation();
  const navigate = useNavigate();

  // We want to correctly match the URL pathname with the links in the sidebar
  // So something like /home/bill-summary will still match /home so the link will stay active
  const isActive = !!location.pathname.match(`^\\${props.to}`)?.length || false;

  return (
    <TouchableOpacity
      accessibilityRole="link"
      href={props.to}
      onPress={(event) => {
        event.preventDefault();

        props.onPress?.();

        if (props.openInNewTab) {
          const newTab = window.open(props.to, '_blank');

          newTab?.location;

          return;
        }

        props.to && navigate(props.to);
      }}
      style={isBigScreen && styleBigScreen.navigationLinkWrapper}
      nativeID={props.id}
    >
      <View
        style={[
          style.navigationLink,
          isBigScreen && [
            styleBigScreen.navigationLink,
            isActive && styleBigScreen.navigationLinkActive,
          ],
        ]}
      >
        <Image
          source={isActive ? props.iconActive : props.icon}
          style={[
            style.navigationLinkImage,
            isBigScreen && styleBigScreen.navigationLinkImage,
          ]}
        />
        <Text
          style={[
            style.navigationLinkText,
            isBigScreen && styleBigScreen.navigationLinkText,
            isActive && style.navigationLinkTextActive,
          ]}
        >
          {props.text}
        </Text>
      </View>
    </TouchableOpacity>
  );
};

const style = StyleSheet.create({
  root: {
    height: 60,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: 'white',
    justifyContent: 'center',
  },
  logoNonPlus: {
    width: 110,
    height: 40,
  },
  logoPlus: {
    width: 92,
    height: 26,
    marginBottom: 24,
  },
  plusBadge: {
    width: 94,
    height: 16,
  },
  navigationLinks: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  navigationLink: {
    alignItems: 'center',
  },
  navigationLinkImage: {
    width: 24,
    height: 24,
    marginBottom: 4,
  },
  navigationLinkText: {
    color: '101825',
    fontSize: 10,
  },
  navigationLinkTextActive: {
    color: '#E5258C',
  },
  buttonTitle: {
    fontSize: 12,
  },
  downloadButton: {
    height: 40,
  },
});

const styleBigScreen = StyleSheet.create({
  root: {
    height: 'auto',
    backgroundColor: 'transparent',
    justifyContent: 'flex-start',
    width: 160,
  },
  navigationLinks: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    marginVertical: 24,
  },
  navigationLinkWrapper: {
    marginBottom: 8,
  },
  navigationLink: {
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    flexDirection: 'row',
    height: 40,
  },
  navigationLinkActive: {
    backgroundColor: 'white',
  },
  navigationLinkImage: {
    width: 24,
    height: 24,
    marginRight: 8,
    marginBottom: 0,
  },
  navigationLinkText: {
    fontSize: 12,
  },
});

export default Navigation;
